import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { API_BASE, IS_LITE, LITE_TOKEN } from "../../app/const"
import { RootState } from "../../app/store"
import { fireauth } from "../auth/AuthSlice"


export const defaultPageContent = {
  content: [],
  pageable: {
    sort: {
      sorted: false,
      unsorted: false,
      empty: false
    },
    offset: 0,
    pageSize: 0,
    pageNumber: 0,
    unpaged: false,
    paged: false
  },
  last: false,
  totalElements: 0,
  totalPages: 0,
  size: 0,
  number: 0,
  sort: {
    sorted: false,
    unsorted: false,
    empty: false
  },
  numberOfElements: 0,
  first: false,
  empty: false
}

export interface Order {
  direction: 'ASC' | 'DESC'
  property: string
  ignoreCase?: boolean
  nullHandling?: 'NATIVE' | 'NULLS_FIRST' | 'NULLS_LAST'
}
export interface Sort {
  sorted: boolean
  unsorted?: boolean
  empty?: boolean
  orders: Order[]
}
export interface Pageable {
  page: number
  size: number
  sort?: Sort | string
}
export interface Page<T> {
  content: T[]
  pageable: {
    sort: {
      sorted: boolean
      unsorted: boolean
      empty: boolean
    }
    offset: number
    pageSize: number
    pageNumber: number
    unpaged: boolean
    paged: boolean
  }
  last: boolean
  totalElements: number
  totalPages: number
  size: number
  number: number
  sort: {
    sorted: boolean
    unsorted: boolean
    empty: boolean
  }
  numberOfElements: number
  first: boolean
  empty: boolean
}

export interface Config {
  appConfigId: number
  key: string
  value: string
  version: number
  description?: string
}

export interface AdminState {
  status: 'idle' | 'loading' | 'failed'
  config: Config
  configs: Page<Config>
  estimateCache?: string[]
}

const initialState: AdminState = {
  status: 'idle',
  config: {
    appConfigId: 0,
    key: '',
    value: '',
    version: 0
  },
  configs: defaultPageContent,
}

export const fetchAllConfigsAsync = createAsyncThunk(
  'account/fetchAllConfigsAsync', async ({ mode }: { mode: string }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/config/${mode}/all?size=500`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const fetchConfigDetailsAsync = createAsyncThunk(
  'account/fetchConfigDetailsAsync', async ({ slug, mode }: { slug: string, mode: string }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/config/${mode}/bys/${slug}`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const saveConfigAsync = createAsyncThunk(
  'admin/saveConfigAsync', async ({ config, mode }: { config: Config, mode: string }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.post(`${API_BASE}apps/config/${mode}`, {
        appConfigId: config.appConfigId,
        key: config.key,
        value: config.value,
        version: config.version,
        description: config.description
      }, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)


export const getAllEstimateCacheAsync = createAsyncThunk('admin/getAllEstimateCacheAsync',
  async ({ pattern }: { pattern: string }, { getState }) => {
    const state = getState() as RootState
    return axios.post(`${API_BASE}apps/cache/estimate/list`, {
      pattern
    }, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
      }
    })
  })

export const deleteEstimateCacheAsync = createAsyncThunk('admin/deleteEstimateCacheAsync',
  async ({ key }: { key: string }, { getState }) => {
    const state = getState() as RootState
    return axios.post(`${API_BASE}apps/cache/estimate/del`, {
      key
    }, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
      }
    })
  })

export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    resetConfig: (state) => {
      state.status = 'idle'
      state.config = initialState.config
    },
    pickConfig: (state, action) => {
      state.status = 'idle'
      state.config = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.
      addCase(fetchAllConfigsAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchAllConfigsAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.configs = action.payload
      }).addCase(fetchAllConfigsAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.configs = defaultPageContent
      }).addCase(fetchConfigDetailsAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchConfigDetailsAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.config = action.payload
      }).addCase(fetchConfigDetailsAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.config = initialState.config
      }).addCase(saveConfigAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(saveConfigAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.config = initialState.config
      }).addCase(saveConfigAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.config = initialState.config
      }).addCase(getAllEstimateCacheAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(getAllEstimateCacheAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.estimateCache = (action.payload as any).data
      }).addCase(getAllEstimateCacheAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.estimateCache = []
      }).addCase(deleteEstimateCacheAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(deleteEstimateCacheAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.estimateCache = state.estimateCache?.filter((elem) => !(action.payload as any).data.includes(elem))
      }).addCase(deleteEstimateCacheAsync.rejected, (state, action) => {
        state.status = 'failed'
      })
  }
})

export const { resetConfig, pickConfig } = adminSlice.actions
export const selectStatus = (state: RootState) => state.admin.status
export const selectAllConfig = (state: RootState) => state.admin.configs
export const selectConfig = (state: RootState) => state.admin.config
export const selectEstimateCache = (state: RootState) => state.admin.estimateCache
export default adminSlice.reducer
